import { ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { messagesSelectors } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { useModal } from 'providers/ModalProvider'
import { ExportChatModal } from 'components/modals/ExportChatModal'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

type ChatPageHeaderProps = {
  /** Extra buttons before the menu, inline in the header row (notebook: the prompts panel toggle). */
  actions?: ReactNode
}

export const ChatPageHeader = ({ actions }: ChatPageHeaderProps) => {
  const { t } = useTranslation()
  const currentChat = useSelector(messagesSelectors.currentChat)
  const isInOperatorMode = useSelector(messagesSelectors.currentChatIsInOperatorMode)
  const isLogged = useSelector(settingsSelectors.isLogged)
  const { openModal } = useModal()

  const dropdownMenu = useMemo(() => {
    return _.compact([
      isLogged && {
        title: t('exportChat'),
        icon: <OdinIcon icon={['far', 'file-export']} className="h-4 w-4" />,
        callback: () => openModal(<ExportChatModal />),
      },
    ])
  }, [isLogged, t, openModal])

  return (
    <div className="relative flex h-[60px] shrink-0 items-center gap-2 border-b border-border bg-background px-3">
      <SidebarTrigger className="h-9 w-9 text-foreground" />

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate text-sm font-semibold text-foreground">
          {currentChat?.ticket?.title || t('chat')}
        </span>
        {isInOperatorMode && (
          <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {t('operatorMode')}
          </span>
        )}
      </div>

      {actions}

      {dropdownMenu.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-accent">
            <OdinIcon icon={['far', 'ellipsis-vertical']} className="h-4 w-4 text-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-auto">
            {dropdownMenu.map((item, idx) => (
              <DropdownMenuItem key={idx} onClick={item.callback}>
                {item.icon}
                <span>{item.title}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
